/* eslint-disable jsx-a11y/no-redundant-roles */
/* eslint-disable jsx-a11y/role-supports-aria-props */
import { HeadPhoneGuideModal } from '@/components/common/head-phone-guide-modal';
import { LanguageDropdown, type LanguageDropdownHandle } from '@/components/common/language-dropdown';
import { VoiceGuideModal } from '@/components/common/modal-voice-guide';
import { SpeakerCloseModal } from '@/components/common/speaker-close-modal';
import { ZoomDialog } from '@/components/common/zoom-dialog';
import { ClockIcon } from '@/components/icons/ClockIcon';
import FooterBackIcon from '@/components/icons/footer/BackIcon';
import { useAccessibilityTTS, useTickSoundInteraction, useVolumeButton } from '@/hooks';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useFnbStore, useShopStore, type languageType } from '@/stores';
import { CONFIG_CONSTANTS, defaultConfig, buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { resetKioskSessionToHomeScreensaver } from '@/utils/kioskSessionReset';
import { willBackGoToHome } from '@/utils/navigationHistory';
import { formatOperatingHours, getActivityTimeTTS } from '@/utils/time';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './FooterLayout.module.scss';
import { KEY_CODE_SIHEUNG } from '@/constants/app';
import { NAV_KEYS } from '@/constants/keyboard';
import { useOrientation } from '@/hooks/useOrientation';
interface FooterLayoutProps {
    onZoomButtonsOpen?: (isOpen: boolean) => void;
    isZoomButtonsOpen?: boolean;
    handleZoomClose?: () => void;
    onBarrierFreeUsed?: () => void;
    closeModal?: (closeFunction: () => void) => void;
    onBarrierFreeReset?: () => void;
}

export const FooterLayout: React.FC<FooterLayoutProps> = ({
    onZoomButtonsOpen,
    isZoomButtonsOpen,
    handleZoomClose,
    onBarrierFreeUsed,
    closeModal,
    onBarrierFreeReset,
}) => {
    // built-in hooks
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const openTime = useConfigStore((state) => state.config.ui.openTime);
    const closeTime = useConfigStore((state) => state.config.ui.closeTime);
    const tts = useConfigStore((state) => state.config.tts);
    const zoomIn = useConfigStore((state) => state?.config?.ui?.zoomIn);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const lotteEvents = useConfigStore((state) => state.config.server?.building?.lotteEvents);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const setFnbKeyboardLanguage = useFnbStore((state) => state.setKeyboardLanguage);
    const setShopKeyboardLanguage = useShopStore((state) => state.setKeyboardLanguage);
    const language = useConfigStore((state) => state.config.ui.language);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    // custom hooks
    const { isHorizontal } = useOrientation();
    const { speak } = useAccessibilityTTS();
    const { playTick } = useTickSoundInteraction();
    const { volume, handleVolumeChange, handleVolumeFocus, handleVolumeKeyDown, isVolumeChanged } = useVolumeButton(onBarrierFreeUsed);

    // local states
    const [isZoomDialogOpen, setIsZoomDialogOpen] = useState(false);
    const [isShowHeadPhoneGuide, setIsShowHeadPhoneGuide] = useState(false);
    const [isShowSpeakerClose, setIsShowSpeakerClose] = useState(false);
    const [isShowVoiceGuide, setIsShowVoiceGuide] = useState(false);

    const languageDropdownRef = useRef<LanguageDropdownHandle>(null);
    const isHomePage = location.pathname === PATHS.HOME.INDEX;
    const isSiheungVertical = KEY_CODE_SIHEUNG.includes(storeCode) && !isHorizontal;

    const isHeadphoneConnected = tts?.voice === true && tts?.speaker === 'headphone';

    // Get TTS parameters for activity time
    const activityTimeTTSParams = useMemo(() => getActivityTimeTTS(openTime, closeTime, language), [openTime, closeTime, language]);

    // Memoized handlers
    const handleConfigUpdate = useCallback(
        (updates: Partial<typeof ui>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [ui, updateConfig]
    );

    const handleContrastToggle = useCallback(() => {
        handleConfigUpdate({ contrast: !ui.contrast });
        onBarrierFreeUsed?.();
    }, [ui.contrast, handleConfigUpdate]);

    const handleScreenModeToggle = useCallback(() => {
        const newMode = ui.screenMode === 'fullscreen' ? 'split' : 'fullscreen';
        handleConfigUpdate({ screenMode: newMode });
        onBarrierFreeUsed?.();
    }, [ui.screenMode, handleConfigUpdate, onBarrierFreeUsed]);

    const handleZoomToggle = useCallback(() => {
        if (isZoomButtonsOpen) {
            handleZoomClose?.();
            return;
        }
        setIsZoomDialogOpen(true);
        onBarrierFreeUsed?.();
        TrackingService.onHomePopupOpen();
    }, [isZoomButtonsOpen, handleZoomClose, onBarrierFreeUsed]);

    const handleVoiceChange = useCallback(() => {
        if (tts.voice) {
            if (tts.speaker === 'headphone') {
                setIsShowHeadPhoneGuide(true);
            } else {
                setIsShowSpeakerClose(true);
            }
        } else {
            if (!tts.voice) {
                updateConfig({ tts: { ...tts, voice: true } });
                setIsShowVoiceGuide(true);
                onBarrierFreeUsed?.();
            }
        }
    }, [tts, onBarrierFreeUsed]);

    const handleOnConfirmSpeakerCloseModal = useCallback(() => {
        setIsShowSpeakerClose(false);
        const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        // Get menu items using buildMenuFromBuildingMenus
        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, currentLanguage);

        // Change i18n language to Korean
        i18n.changeLanguage(currentLanguage);

        updateConfig({
            ui: {
                ...ui,
                language: currentLanguage,
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage),
                zoomIn: false,
                zoomClosed: true,
                contrast: false,
                volume: 3,
                screenMode: 'fullscreen',
                zoomLevel: 1,
            },
            tts: {
                ...tts,
                voice: false,
            },
        });

        // Reset keyboard language to Korean
        setFnbKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');
        setShopKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');

        onZoomButtonsOpen?.(false);
        navigate(PATHS.HOME.INDEX);
    }, [tts, ui, buildingMenus, lotteEvents, i18n, updateConfig, navigate, onZoomButtonsOpen, setFnbKeyboardLanguage, setShopKeyboardLanguage, storeLangs]);

    const handleOnConfirmVoiceGuideModal = useCallback(() => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: false } });
    }, [tts, updateConfig]);

    const handleOnCloseVoiceGuideModal = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: false } });
    };
    const handleOnConfirmAndNotRedirectHome = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: true } });
    };

    const handleZoomDialogClose = () => {
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(false);
        TrackingService.onHomePopupClose();
        onBarrierFreeReset?.();
    };

    const handleZoomDialogConfirm = () => {
        onBarrierFreeUsed?.();
        handleConfigUpdate({ zoomIn: !zoomIn, zoomClosed: false, zoomLevel: 2 });
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(true);
        TrackingService.onHomePopupClose();
    };

    // Function to close all modals - used by CountdownButton
    const handleCloseAllModals = useCallback(() => {
        setIsZoomDialogOpen(false);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowVoiceGuide(false);
        languageDropdownRef.current?.close();
    }, []);

    const handleBackClick = useCallback(() => {
        playTick();
        if (willBackGoToHome() && !isHeadphoneConnected) {
            onZoomButtonsOpen?.(false);
            handleCloseAllModals();
            resetKioskSessionToHomeScreensaver(navigate);
            onBarrierFreeReset?.();
            return;
        }
        navigate(-1);
    }, [playTick, navigate, onZoomButtonsOpen, onBarrierFreeReset, handleCloseAllModals]);

    // Expose closeModal function to parent component
    useEffect(() => {
        if (closeModal) {
            closeModal(handleCloseAllModals);
        }
    }, [closeModal, handleCloseAllModals]);

    const AccessibilityButtons = useMemo(() => {
        return (
            <div
                className="flex items-center gap-[calc(12*var(--vw-unit))]"
                data-region-nav={isHomePage ? true : undefined}
                data-region-order={isHomePage ? 2 : undefined}
            >
                {ui.barrierFree && (
                    <button
                        type="button"
                        role="switch"
                        aria-checked={tts?.voice !== defaultConfig.tts.voice}
                        aria-valuetext={t('footer.barrier_free.voice_tts')}
                        data-playsound="ting"
                        data-valuenow={tts?.voice}
                        data-interactive
                        onClick={handleVoiceChange}
                        className={styles['footer__actions__button']}
                    >
                        <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.voice')}</span>
                    </button>
                )}

                <button
                    data-tts-scope="volume-button"
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.toggle_button')}
                    aria-checked={isVolumeChanged}
                    aria-label={t('footer.barrier_free.volume')}
                    data-valuenow={volume}
                    data-volumn-level={volume}
                    data-interactive
                    onClick={handleVolumeChange}
                    onFocus={handleVolumeFocus}
                    onKeyDown={handleVolumeKeyDown}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>
                        {isSiheungVertical ? t('footer.barrier_free.volume') : t('footer.barrier_free.volume') + ` ${volume}`}
                    </span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.contrast}
                    data-valuenow={ui.contrast}
                    aria-valuetext={t('footer.barrier_free.contrast_tts')}
                    data-interactive
                    onClick={handleContrastToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.contrast')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.switch_button')}
                    aria-checked={ui.zoomIn}
                    aria-valuetext={t('footer.barrier_free.zoom_in_tts')}
                    data-interactive
                    data-playsound="ting"
                    onClick={handleZoomToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.zoom_in')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.screenMode !== 'fullscreen'}
                    aria-valuetext={t('footer.barrier_free.screen_mode_tts')}
                    data-interactive
                    onClick={handleScreenModeToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.screen_mode')}</span>
                </button>
            </div>
        );
    }, [
        ui.barrierFree,
        ui.contrast,
        ui.screenMode,
        ui.zoomIn,
        volume,
        tts,
        t,
        handleContrastToggle,
        handleScreenModeToggle,
        handleZoomToggle,
        handleVolumeChange,
        handleVoiceChange,
        isHomePage,
        isSiheungVertical,
    ]);

    const isShowTime = useMemo(() => {
        return !!openTime && !!closeTime;
    }, [openTime, closeTime]);

    return (
        <footer className={styles['footer']} role="contentinfo" data-nav="footer">
            <div className={styles['footer__content']}>
                {isHomePage ? (
                    isShowTime ? (
                        <div className={styles['footer__info']}>
                            <div
                                role="list"
                                tabIndex={0}
                                className={styles['footer__info__open'] + ' focus-border'}
                                data-region-nav
                                data-region-order={1}
                                aria-label={`${t('footer.activity_time')} ${t('footer.activity_time_tts', activityTimeTTSParams)}`}
                                data-interactive
                            >
                                <ClockIcon />
                                <span className={styles['footer__info__open__time']}>
                                    {t('footer.activity_time')}
                                    <span className={clsx(styles['footer__info__open__time--hour'], 'number')}>
                                        {formatOperatingHours(openTime, closeTime)}
                                    </span>
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className={styles['footer__info']}></div>
                    )
                ) : (
                    <div className={styles['footer__actions']}>
                        <button
                            data-region-nav
                            type="button"
                            className={clsx(styles['footer__actions__button'], styles['back'])}
                            onClick={handleBackClick}
                            onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                if (NAV_KEYS.includes(e.key)) {
                                    speak(t('common.data_role.screen_back'));
                                }
                            }}
                        >
                            <FooterBackIcon />
                            <span className={styles['footer__actions__button__label']}>{t('footer.back')}</span>
                        </button>
                    </div>
                )}
                <div className={styles['footer__actions']} data-region-nav={isHomePage ? undefined : true}>
                    {AccessibilityButtons}
                    <LanguageDropdown
                        ref={languageDropdownRef}
                        onBarrierFreeUsed={onBarrierFreeUsed}
                        onZoomButtonsOpen={onZoomButtonsOpen}
                        regionNav={isHomePage}
                        regionOrder={isHomePage ? 3 : undefined}
                        variant={isSiheungVertical ? 'pills' : 'dropdown'}
                    />
                </div>
            </div>
            <ZoomDialog isOpen={isZoomDialogOpen} onClose={handleZoomDialogClose} onConfirm={handleZoomDialogConfirm} />
            <SpeakerCloseModal isOpen={isShowSpeakerClose} onClose={() => setIsShowSpeakerClose(false)} onConfirm={handleOnConfirmSpeakerCloseModal} />
            <HeadPhoneGuideModal isOpen={isShowHeadPhoneGuide} onClose={() => setIsShowHeadPhoneGuide(false)} />
            <VoiceGuideModal
                isOpen={isShowVoiceGuide}
                onClose={handleOnCloseVoiceGuideModal}
                onConfirm={handleOnConfirmVoiceGuideModal}
                onConfirmAndNotRedirectHome={handleOnConfirmAndNotRedirectHome}
            />
        </footer>
    );
};

export default FooterLayout;
